/**
 * Post-game feedback card (#200, FR-16.1; DI-A thumbs, DI-D behavior). Rendered under the
 * GAME_OVER standings for **players + the host only** — never the presentation/TV screen
 * (the mounting pages decide that). A thumbs up/down + an optional comment, submitted in
 * ONE action (FR-9.5), skippable via "No thanks", shown **once per game**: a submit OR a
 * dismiss records a per-`gameId` flag in `localStorage`, so a reconnect to an already-rated
 * game-over never re-nags (DI-D). The thank-you is announced to assistive tech.
 *
 * Presentational + self-contained: it reads the (optional) signed guest identity and POSTs
 * to the shared `/api/feedback` route. Feedback never touches the game wire (D200.5).
 */

import { useState } from 'react';
import type { FeedbackRating } from '@jeopardy/shared';
import { BRAND_NAME } from './brand';
import { loadIdentity } from '../net/auth';
import { submitFeedback } from '../net/feedback-api';
import { ApiError } from '../net/api-error';

export interface PostGameFeedbackProps {
  /** The game being rated — keys the once-per-game flag + is stored with the feedback. */
  gameId: string;
}

/** `localStorage` key for the once-per-game "already rated/dismissed" flag (DI-D). */
function seenKey(gameId: string): string {
  return `peril:feedback:${gameId}`;
}

function alreadyHandled(gameId: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(seenKey(gameId)) === 'done';
}

function markHandled(gameId: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(seenKey(gameId), 'done');
  } catch {
    // A storage failure (private mode / quota) just means we might re-prompt — harmless.
  }
}

export function PostGameFeedback({
  gameId,
}: PostGameFeedbackProps): React.JSX.Element | null {
  // Initialize from persisted state so a reconnect to an already-handled game-over stays
  // hidden without a flash (DI-D reconnect-safe).
  const [done, setDone] = useState<boolean>(() => alreadyHandled(gameId));
  const [rating, setRating] = useState<FeedbackRating | undefined>(undefined);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Once handled (this session or a prior one) and not showing the thank-you, render
  // nothing — the card is one-shot per game.
  if (done && !submitted) return null;

  if (submitted) {
    return (
      <div
        className="fb-thanks"
        role="status"
        aria-live="polite"
        data-testid="feedback-thanks"
      >
        <span className="fb-thanks__check" aria-hidden="true">
          ✓
        </span>
        <div>
          <div className="fb-thanks__title">Thanks for the feedback!</div>
          <div className="fb-thanks__sub">
            Your note helps us make {BRAND_NAME} better. We read everything.
          </div>
        </div>
      </div>
    );
  }

  const dismiss = (): void => {
    markHandled(gameId);
    setDone(true);
  };

  const send = async (): Promise<void> => {
    if (rating === undefined || submitting) return;
    setSubmitting(true);
    setError(undefined);
    try {
      await submitFeedback(
        {
          type: 'rating',
          surface: 'post-game',
          rating,
          ...(text.trim() ? { text: text.trim() } : {}),
          gameId,
        },
        loadIdentity()?.guestToken,
      );
      markHandled(gameId);
      setSubmitted(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not send your feedback. Please try again.',
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="rating-card"
      role="group"
      aria-label="Rate this game"
      data-testid="post-game-feedback"
    >
      <p className="rating-card__q">How was this game?</p>
      <div className="thumbs" role="radiogroup" aria-label="Thumbs rating">
        <button
          type="button"
          className={'thumb' + (rating === 'up' ? ' thumb--up-on' : '')}
          role="radio"
          aria-checked={rating === 'up'}
          aria-label="Thumbs up"
          data-testid="feedback-thumb-up"
          onClick={() => setRating('up')}
        >
          👍
        </button>
        <button
          type="button"
          className={'thumb' + (rating === 'down' ? ' thumb--down-on' : '')}
          role="radio"
          aria-checked={rating === 'down'}
          aria-label="Thumbs down"
          data-testid="feedback-thumb-down"
          onClick={() => setRating('down')}
        >
          👎
        </button>
      </div>
      <div className="fb-field">
        <label htmlFor="post-game-feedback-text">
          Anything you’d like to tell us? <span className="muted">(optional)</span>
        </label>
        <textarea
          id="post-game-feedback-text"
          data-testid="feedback-text"
          placeholder="What did you love? What could be better? Ideas welcome…"
          value={text}
          maxLength={2000}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      {error && (
        <p className="fb-error" role="alert" data-testid="feedback-error">
          {error}
        </p>
      )}
      <div className="fb-actions">
        <button
          type="button"
          className="btn btn--primary"
          data-testid="feedback-send"
          disabled={rating === undefined || submitting}
          onClick={() => void send()}
        >
          {submitting ? 'Sending…' : 'Send feedback'}
        </button>
        <button
          type="button"
          className="btn btn--link"
          data-testid="feedback-dismiss"
          onClick={dismiss}
        >
          No thanks
        </button>
      </div>
      <p className="rating-card__hint">
        Ratings &amp; comments are private and help us improve {BRAND_NAME}.
      </p>
    </div>
  );
}
